import AppBridgeHelper from "helpers/AppBridgeHelper";
import { db } from "helpers/DBHelper";
import { COLLECTIONS, EVENTS, PRINTER_RESPONSES } from "const";
import { eventsEmitter } from "utils/events";

export interface PrintJob {
  id?: number;
  label: string;
  targetKey: string;
  printerType: string;
  printerTarget: string;
  content: string; // Base64 encoded byte stream
  status: "processing" | "failed";
  retryCount: number;
  createdAt: string;
}

export interface PrintParams {
  content: string;
  printerType: string;
  printerTarget: string;
  label: string;
}

class PrinterManagerClass {
  // Map of queues per target key: e.g. { 'LAN_192.168.1.33': [job1, job2], 'USB_PRINTER': [job3] }
  public queues: Map<string, PrintJob[]>;
  // Active processing locks per target key
  private activeLocks: Set<string>;
  // Retry interval timers per target key
  private retryTimers: Map<string, NodeJS.Timeout>;

  constructor() {
    this.queues = new Map<string, PrintJob[]>();
    this.retryTimers = new Map();
    this.activeLocks = new Set();
  }

  public start(): void {
    this.loadQueuesFromStorage();
  }

  private notifyListeners(): void {
    eventsEmitter.emit(EVENTS.PRINTER_QUEUE_UPDATED, new Map(this.queues));
  }

  public stopAllQueues(): void {
    this.retryTimers.forEach((timer) => {
      clearInterval(timer);
    });
    this.retryTimers.clear();
  }

  public startAllQueues(): void {
    this.processAllQueues();
  }

  public async clearAllQueues(): Promise<void> {
    this.stopAllQueues();
    this.queues.clear();
    try {
      await db.clear(COLLECTIONS.PRINT_ERROR_JOBS.name);
    } catch (error) {
      console.error("[PrinterManager] Failed to clear print jobs from DB:", error);
    }
    this.notifyListeners();
  }

  public async clearPrintJob(jobId: number): Promise<void> {
    this.queues.forEach((queue, targetKey) => {
      const index = queue.findIndex((job) => job.id === jobId || String(job.id) === String(jobId));
      if (index !== -1) {
        queue.splice(index, 1);
        if (queue.length === 0) {
          this.queues.delete(targetKey);
          this.clearRetryTimer(targetKey);
        } else {
          this.queues.set(targetKey, queue);
        }
      }
    });

    try {
      await db.delete(COLLECTIONS.PRINT_ERROR_JOBS.name, jobId);
    } catch (error) {
      console.error(`[PrinterManager] Failed to delete print job ${jobId} from DB:`, error);
    }

    this.notifyListeners();
  }

  /**
   * Main entry point for printing.
   * Attempts direct print first. On failure, queues job for retry.
   * If existing failed jobs or active lock exist for this target printer, queues directly to preserve FIFO order.
   */
  public async print(params: PrintParams): Promise<void> {
    const targetKey = this.getTargetPrinterKey(params.printerType, params.printerTarget);

    // If queue is already processing or has failed jobs, enqueue and trigger queue processing
    if (this.activeLocks.has(targetKey) || this.hasPendingJobs(targetKey)) {
      this.enqueueJobAndProcess(params);
      throw new Error(`Printer ${targetKey} has pending queue. Job added to retry queue.`);
    }

    const bridge = AppBridgeHelper.getBridge();
    if (!bridge) throw new Error("No native bridge found");

    try {
      const result = await bridge.printText(params.content, params.printerType, params.printerTarget);
      if (result && typeof result === "string" && result.startsWith(PRINTER_RESPONSES.ERROR)) {
        throw new Error(result);
      }
    } catch (error) {
      console.error("Failed to print: ", error);
      this.handlePrintFailure(params);
      throw error;
    }
  }

  /**
   * Handle print failure - Add to the queue for retry later.
   */
  public async handlePrintFailure(params: PrintParams): Promise<void> {
    const job = await this.enqueueJob(params);
    this.scheduleRetry(job.targetKey);
  }

  private async enqueueJobAndProcess(params: PrintParams): Promise<void> {
    const job = await this.enqueueJob(params);
    this.processPrinterQueue(job.targetKey);
  }

  private async enqueueJob(params: PrintParams): Promise<PrintJob> {
    const targetKey = this.getTargetPrinterKey(params.printerType, params.printerTarget);
    const job: PrintJob = {
      label: params.label,
      printerType: params.printerType,
      printerTarget: params.printerTarget,
      content: params.content,
      targetKey,
      status: "failed",
      retryCount: 0,
      createdAt: new Date().toISOString(),
    };

    return this.addToQueue(job, true);
  }

  /**
   * Process all queued jobs sequentially for a target printer.
   */
  public async processPrinterQueue(targetKey: string): Promise<void> {
    if (this.activeLocks.has(targetKey)) return;

    const targetQueue = this.queues.get(targetKey);
    if (!targetQueue || targetQueue.length === 0) {
      this.clearRetryTimer(targetKey);
      return;
    }

    const bridge = AppBridgeHelper.getBridge();
    if (!bridge) return;

    try {
      this.activeLocks.add(targetKey);

      while (targetQueue.length > 0) {
        const currentJob = targetQueue[0];

        try {
          currentJob.status = "processing";
          const result = await bridge.printText(currentJob.content, currentJob.printerType, currentJob.printerTarget);

          if (result && typeof result === "string" && result.startsWith(PRINTER_RESPONSES.ERROR)) {
            throw new Error(result);
          }

          // Success: remove job from in-memory queue & DB
          targetQueue.shift();
          if (targetQueue.length === 0) {
            this.queues.delete(targetKey);
          }
          if (currentJob.id) {
            await db.delete(COLLECTIONS.PRINT_ERROR_JOBS.name, currentJob.id);
          }
          this.notifyListeners();
        } catch (err) {
          console.warn(`[PrinterManager] Retry failed for ${targetKey}.`, err);
          currentJob.status = "failed";
          currentJob.retryCount += 1;
          if (currentJob.id) {
            await db.update(COLLECTIONS.PRINT_ERROR_JOBS.name, currentJob.id, currentJob);
          }
          this.notifyListeners();

          // Stop processing THIS printer's queue to preserve FIFO order
          break;
        }
      }
    } finally {
      this.activeLocks.delete(targetKey);

      if (targetQueue.length > 0) {
        this.scheduleRetry(targetKey);
      } else {
        this.clearRetryTimer(targetKey);
      }
    }
  }

  /**
   * Process all queues across all printers.
   */
  public processAllQueues(): void {
    this.queues.forEach((_, targetKey) => {
      this.processPrinterQueue(targetKey);
    });
  }

  public hasPendingJobs(targetKey: string): boolean {
    const queue = this.queues.get(targetKey);
    return !!(queue && queue.length > 0);
  }

  private scheduleRetry(targetKey: string, delayMs: number = 10000): void {
    if (this.retryTimers.has(targetKey)) return;

    const timer = setInterval(() => {
      this.processPrinterQueue(targetKey);
    }, delayMs);

    this.retryTimers.set(targetKey, timer);
  }

  private clearRetryTimer(targetKey: string): void {
    const timer = this.retryTimers.get(targetKey);
    if (timer) {
      clearInterval(timer);
      this.retryTimers.delete(targetKey);
    }
  }

  /**
   * Helper to derive unique target key (e.g., 'LAN_192.168.1.33', 'USB_DEFAULT')
   */
  public getTargetPrinterKey(printerType: string, printerTarget?: string): string {
    return `${printerType}_${printerTarget || "DEFAULT"}`.toUpperCase();
  }

  private async addToQueue(job: PrintJob, persist = false): Promise<PrintJob> {
    const { targetKey } = job;

    if (persist) {
      const generatedId = await db.add(COLLECTIONS.PRINT_ERROR_JOBS.name, job);
      job.id = generatedId as number;
    }

    // Push to in-memory queue with its newly assigned ID
    const targetQueue = this.queues.get(targetKey) || [];
    targetQueue.push(job);
    this.queues.set(targetKey, targetQueue);
    this.notifyListeners();

    return job;
  }

  private async loadQueuesFromStorage(): Promise<void> {
    try {
      const storedJobs: PrintJob[] = await db.getAll(COLLECTIONS.PRINT_ERROR_JOBS.name);
      if (!storedJobs || storedJobs.length === 0) return;

      this.queues = new Map();
      storedJobs.forEach((job) => {
        const queue = this.queues.get(job.targetKey) || [];
        queue.push(job);
        this.queues.set(job.targetKey, queue);
      });
      this.notifyListeners();

      // Resume retries & attempt immediate processing for loaded queues
      this.processAllQueues();
    } catch (error) {
      console.error("[PrinterManager] Failed to load print queue from storage:", error);
    }
  }
}

export const PrinterManager = new PrinterManagerClass();
